import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Search,
  Filter,
  CalendarDays,
  Loader2,
  CheckCircle2,
  XCircle,
  X,
  KeyRound,
  UserCog,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/ui/PasswordInput";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]";

const PROVIDER_THEME = {
  moncash: { label: "MonCash", bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  natcash: { label: "NatCash", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);
const fmtCash = (n, c) => {
  const x = Number(n || 0);
  if (NO_DECIMAL.has(c)) return `${Math.round(x).toLocaleString("en-US")} ${c}`;
  return `${x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
};

function StatusPill({ status }) {
  const map = {
    SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
    FAILED: "bg-rose-50 text-rose-700 border-rose-200",
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {status}
    </span>
  );}

function ProofThumb({ url, reference }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let objectUrl = "";
    const load = async () => {
      if (!url) return;
      setLoading(true);
      try {
        const path = url.replace(/^\/api/, "");
        const resp = await api.get(path, { responseType: "blob" });
        objectUrl = URL.createObjectURL(resp.data);
        setBlobUrl(objectUrl);
        setErr(false);
      } catch {
        setErr(true);
      } finally { setLoading(false); }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  const openInTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank", "noopener");
  };

  if (err) {
    return <span className="text-[11px] text-rose-500 italic">Unavailable</span>;
  }
  return (
    <div className="flex items-center gap-2">
      {loading || !blobUrl ? (
        <div className="w-10 h-10 rounded border border-slate-200 bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
        </div>
      ) : (
        <button
          type="button"
          onClick={openInTab}
          data-testid={`proof-thumb-${reference}`}
          title="Open proof in a new tab"
          className="focus:outline-none focus:ring-2 focus:ring-[#0F62FE] rounded"
        >
          <img src={blobUrl} alt="proof" className="w-10 h-10 object-cover rounded border border-slate-200 hover:ring-2 hover:ring-[#0F62FE] transition cursor-zoom-in" />
        </button>
      )}
      <button
        type="button"
        onClick={openInTab}
        disabled={!blobUrl}
        data-testid={`proof-view-${reference}`}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 text-slate-700 text-[11px] font-semibold hover:bg-slate-50 disabled:opacity-40"
      >
        <ShieldCheck className="w-3 h-3" /> View Proof
      </button>
    </div>
  );
}

function KpiBox({ label, value, tone, testid, extra }) {
  const tones = {
    emerald: "bg-emerald-500 text-white",
    "emerald-soft": "bg-emerald-50 border-emerald-200 text-emerald-900",
    violet: "bg-violet-500 text-white",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  };
  const cls = tones[tone] || "bg-slate-50 border-slate-200 text-slate-900";
  const dark = tone === "emerald" || tone === "violet";
  return (
    <div data-testid={testid} className={`rounded-md p-3 ${cls} ${!dark ? "border" : ""}`}>
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${dark ? "opacity-90" : "opacity-70"}`}>{label}</div>
      <div className="text-xl font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {extra && <div className={`text-[10px] mt-0.5 ${dark ? "opacity-80" : "opacity-60"}`}>{extra}</div>}
    </div>
  );
}

function VerifyModal({ tx, onClose, onDone, currentUser }) {
  const isVerifierUser = currentUser?.role === "CASH_VERIFIER";
  const [status, setStatus] = useState("SUCCESS");
  const [verifierProvider, setVerifierProvider] = useState(tx.wallet_provider || "");
  // For verifier users, username auto-fills from their account (backend will override too)
  const [verifierUsername, setVerifierUsername] = useState(isVerifierUser ? (currentUser?.username || "") : "");
  const [reason, setReason] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [proofPreview, setProofPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  const cameraRef = React.useRef(null);

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/send-cash/${tx.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.url);
      setProofPreview(URL.createObjectURL(file));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload proof");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (status === "FAILED" && !reason.trim()) {
      toast.error("Please provide a rejection reason"); return;
    }
    if (status === "SUCCESS" && !proofUrl) {
      toast.error("Proof photo is required to confirm this transaction"); return;
    }
    if (status === "SUCCESS" && !isVerifierUser && !verifierUsername.trim()) {
      toast.error("Please enter the verifier account that confirmed this transaction"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/send-cash/${tx.id}/status`, {
        status,
        verifier_provider: verifierProvider || tx.wallet_provider,
        verifier_username: verifierUsername.trim() || null,
        proof_photo_url: proofUrl || null,
        reason: reason.trim() || null,
      });
      toast.success(`${tx.reference} marked ${status}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Verify {tx.reference}
        </div>
        <div className="text-xs text-slate-500 mb-4">
          {tx.recipient_name} · {tx.recipient_phone} · {fmtCash(tx.amount_sent || tx.amount, tx.currency)} → {fmtCash(tx.amount_received, "HTG")}
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Decision</label>
            <div className="flex gap-2">
              <button type="button" data-testid="verify-confirm-btn"
                onClick={() => setStatus("SUCCESS")}
                className={`flex-1 h-10 rounded-md border text-sm font-semibold ${status === "SUCCESS" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                Confirm
              </button>
              <button type="button" data-testid="verify-reject-btn"
                onClick={() => setStatus("FAILED")}
                className={`flex-1 h-10 rounded-md border text-sm font-semibold ${status === "FAILED" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                Reject
              </button>
            </div>
          </div>
          {status === "SUCCESS" && (
            <>
              {!isVerifierUser && (
                <>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Verifier provider</label>
                    <div className="flex gap-2">
                      {["moncash", "natcash"].map((p) => (
                        <button key={p} type="button"
                          onClick={() => setVerifierProvider(p)}
                          className={`flex-1 h-10 rounded-md border text-sm font-semibold ${verifierProvider === p ? "bg-[#0F62FE] text-white border-[#0F62FE]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                          {p === "moncash" ? "MonCash" : "NatCash"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                      Verifier account username
                    </label>
                    <input data-testid="verify-username-input" value={verifierUsername} onChange={(e) => setVerifierUsername(e.target.value)} className={inputCls} placeholder="The MonCash/NatCash account that confirmed the transfer" />
                  </div>
                </>
              )}
              {/* Proof photo — REQUIRED for SUCCESS */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                  Proof photo <span className="text-rose-500">*</span>
                </label>
                <div className="text-[11px] text-slate-500 mb-2">Phone photo, screenshot, or transaction confirmation image.</div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="proof-file-input"
                  onChange={(e) => uploadProof(e.target.files?.[0])} />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="proof-camera-input"
                  onChange={(e) => uploadProof(e.target.files?.[0])} />
                {!proofPreview && !proofUrl ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="proof-take-photo"
                      onClick={() => cameraRef.current?.click()} disabled={uploading}
                      className="h-11 rounded-md border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      📷 Take photo
                    </button>
                    <button type="button" data-testid="proof-upload"
                      onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="h-11 rounded-md border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      📁 Upload image
                    </button>
                  </div>
                ) : (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
                    {proofPreview && <img src={proofPreview} alt="Proof preview" className="w-full max-h-48 object-contain rounded" />}
                    <div className="flex items-center justify-between mt-2 text-[11px]">
                      <span className="font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Proof attached</span>
                      <button type="button" onClick={() => { setProofUrl(""); setProofPreview(""); }} className="text-rose-700 font-semibold hover:underline">
                        Replace
                      </button>
                    </div>
                  </div>
                )}
                {uploading && <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}
              </div>
            </>
          )}
          {status === "FAILED" && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Rejection reason <span className="text-rose-500">*</span>
              </label>
              <textarea data-testid="verify-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                className="flex w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                placeholder="e.g. Wallet not found, balance returned to agent." maxLength={300} />
              <div className="text-[11px] text-rose-700 mt-1">FAILED auto-reverses the debit back to the agent&apos;s Send Cash wallet.</div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="verify-submit-btn"
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-50 ${status === "SUCCESS" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {status === "SUCCESS" ? "Confirm transaction" : "Reject (auto-reverse)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CashVerification() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";
  const isVerifier = user?.role === "CASH_VERIFIER";
  const today = new Date().toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 7 * 86400e3).toISOString().slice(0, 10);

  const [statusFilter, setStatusFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [verifierFilter, setVerifierFilter] = useState(""); // username filter
  const [dateFrom, setDateFrom] = useState(sevenAgo);
  const [dateTo, setDateTo] = useState(today);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(null);

  // Verifier accounts (admin)
  const [verifiers, setVerifiers] = useState([]);
  const [editingVerifier, setEditingVerifier] = useState(null);
  // Verifier KPI dashboard (self)
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadDashboard = useCallback(async () => {
    if (!isVerifier) return;
    try {
      const { data } = await api.get("/verifier/dashboard");
      setDashboard(data);
    } catch { /* ignore */ }
  }, [isVerifier]);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadVerifiers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/cash-verifiers");
      setVerifiers(data || []);
    } catch { /* ignore */ }
  }, [isAdmin]);
  useEffect(() => { loadVerifiers(); }, [loadVerifiers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100", skip: "0" });
      if (statusFilter) params.set("status", statusFilter);
      if (providerFilter) params.set("provider", providerFilter);
      if (verifierFilter) params.set("verifier_username", verifierFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (debouncedSearch) params.set("q", debouncedSearch);
      const { data } = await api.get(`/admin/cash-verification?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("cash_verify.failed_load"));
    } finally { setLoading(false); }
  }, [statusFilter, providerFilter, verifierFilter, dateFrom, dateTo, debouncedSearch, t]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { PENDING: 0, SUCCESS: 0, FAILED: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="cash-verification-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0F62FE]" /> {isAdmin ? `${t("role.MASTER_ADMIN")} · ${t("cash_verify.title")}` : `${t("role.CASH_VERIFIER")} · ${user?.verifier_provider === "moncash" ? "MonCash" : "NatCash"}`}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t("cash_verify.title")}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {t("cash_verify.subtitle")}
          </p>
        </div>
        <Link
          to={isVerifier ? "/profile" : "/send-cash"}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-semibold"
        >
          {isVerifier ? t("cash_verify.back_profile") : t("cash_verify.back_send_cash")}
        </Link>
      </div>

      {/* Verifier self dashboard (CASH_VERIFIER role) */}
      {isVerifier && dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="verifier-dashboard">
          <KpiBox label={t("cash_verify.wallet_balance")} value={fmtCash(dashboard.balance, dashboard.balance_currency)} tone="emerald" testid="kpi-wallet" />
          <KpiBox label={t("cash_verify.commission_earned")} value={fmtCash(dashboard.commission_earned, dashboard.balance_currency)} tone="violet" testid="kpi-commission" extra={t("cash_verify.commission_rate_hint", { pct: (dashboard.commission_rate * 100).toFixed(1) })} />
          <KpiBox label={t("cash_verify.pending")} value={dashboard.pending} tone="amber" testid="kpi-pending-self" />
          <KpiBox label={t("cash_verify.confirmed")} value={dashboard.confirmed} tone="emerald-soft" testid="kpi-confirmed-self" />
          <KpiBox label={t("cash_verify.rejected")} value={dashboard.rejected} tone="rose" testid="kpi-rejected-self" />
        </div>
      )}

      {/* Verifier accounts panel (admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-md border border-slate-200 p-4" data-testid="verifier-accounts-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <UserCog className="w-3 h-3" /> {t("cash_verify.verification_users")}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("cash_verify.dedicated_hint")}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["moncash", "natcash"].map((prov) => {
              const v = verifiers.find((x) => x.provider === prov);
              const themeBg = prov === "moncash" ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200";
              const label = prov === "moncash" ? "MonCash" : "NatCash";
              return (
                <div key={prov} data-testid={`verifier-card-${prov}`} className={`rounded-md border p-3 ${themeBg}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">{t("cash_verify.verifier_label", { provider: label })}</div>
                      <div className="font-mono text-sm font-bold text-slate-900 mt-0.5">{v?.username || t("cash_verify.not_configured")}</div>
                      <div className="text-[11px] text-slate-600 mt-0.5">{v?.name || ""}</div>
                    </div>
                    <button
                      data-testid={`edit-verifier-${prov}`}
                      onClick={() => setEditingVerifier({ id: v?.id, provider: prov, username: v?.username || "", password: "", name: v?.name || "", phone: v?.phone || "", phone_masked: v?.phone_masked || "", commission_rate: v?.commission_rate ?? 0.05, balance: v?.balance ?? 0, balance_currency: v?.balance_currency || "HTG", isUpdate: !!v })}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold hover:bg-slate-50"
                    >
                      <KeyRound className="w-3 h-3" />
                      {v ? t("cash_verify.edit") : t("cash_verify.create")}
                    </button>
                  </div>
                  {v && (
                    <>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("cash_verify.balance")}</div>
                          <div className="font-bold text-slate-900">{fmtCash(v.balance, v.balance_currency)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("cash_verify.commission")}</div>
                          <div className="font-bold text-slate-900">{(v.commission_rate * 100).toFixed(1)}%</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("cash_verify.earned")}</div>
                          <div className="font-bold text-slate-900">{fmtCash(v.commission_earned, v.balance_currency)}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{t("cash_verify.pending")}: {v.pending}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">✓ {v.confirmed}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">✗ {v.rejected}</span>
                      </div>
                      <button
                        data-testid={`filter-verifier-${prov}`}
                        onClick={() => setVerifierFilter(verifierFilter === v.username ? "" : v.username)}
                        className={`mt-2 text-[11px] font-semibold tracking-wider uppercase ${verifierFilter === v.username ? "text-[#0F62FE]" : "text-slate-500 hover:text-[#0F62FE]"}`}
                      >
                        {verifierFilter === v.username ? t("cash_verify.showing_only_verifier") : t("cash_verify.show_only_verifier")}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Counters */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="count-pending">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">{t("cash_verify.pending")}</div>
          <div className="text-3xl font-bold text-amber-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{counts.PENDING}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4" data-testid="count-confirmed">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">{t("cash_verify.confirmed")}</div>
          <div className="text-3xl font-bold text-emerald-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{counts.SUCCESS}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-md p-4" data-testid="count-rejected">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-700">{t("cash_verify.rejected")}</div>
          <div className="text-3xl font-bold text-rose-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{counts.FAILED}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md border border-slate-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{t("cash_verify.status")}</label>
            <div className="flex items-center gap-1">
              {["", "PENDING", "SUCCESS", "FAILED"].map((s) => (
                <button key={s || "all"} data-testid={`filter-status-${s || "all"}`}
                  onClick={() => setStatusFilter(s)}
                  className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 h-9 rounded-md border ${
                    statusFilter === s ? "bg-[#0F62FE] text-white border-[#0F62FE]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}>
                  {s === "SUCCESS" ? t("cash_verify.confirmed") : s === "FAILED" ? t("cash_verify.rejected") : s === "PENDING" ? t("cash_verify.pending") : t("cash_verify.status_all")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{t("cash_verify.provider")}</label>
            <div className="flex items-center gap-1">
              {[{ v: "", l: t("cash_verify.status_all") }, { v: "moncash", l: "MonCash" }, { v: "natcash", l: "NatCash" }].map((p) => (
                <button key={p.v || "all"} data-testid={`filter-provider-${p.v || "all"}`}
                  onClick={() => setProviderFilter(p.v)}
                  className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 h-9 rounded-md border ${
                    providerFilter === p.v ? "bg-[#0F62FE] text-white border-[#0F62FE]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> From
            </label>
            <input type="date" data-testid="date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> To
            </label>
            <input type="date" data-testid="date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9`} placeholder="Reference, recipient, phone, verifier…" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200" data-testid="cash-verification-table-card">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> {total} matching transactions
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cash-verification-table">
            <thead>
              <tr>
                {["Ref", "Created", "Agent", "Full name", "Phone", "Provider", "Sent", "Receives", "Verifier", "Proof", "Status", "Action"].map((h) => (
                  <th key={h} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="px-5 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={12} className="px-5 py-8 text-center text-slate-500 text-sm">No matching transactions.</td></tr>
              )}
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cv-row-${t.reference}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 font-semibold">{t.reference}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-4 py-3 text-slate-700">{t.agent_username || "—"}</td>
                  <td className="px-4 py-3 text-slate-900 font-semibold">{t.recipient_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{t.recipient_phone}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${PROVIDER_THEME[t.wallet_provider]?.bg} ${PROVIDER_THEME[t.wallet_provider]?.text} ${PROVIDER_THEME[t.wallet_provider]?.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${PROVIDER_THEME[t.wallet_provider]?.dot}`} />
                      {t.wallet_provider_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{fmtCash(t.amount_sent || t.amount, t.currency)}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap">
                    {t.amount_received ? fmtCash(t.amount_received, t.receive_currency || "HTG") : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {t.verifier_username ? (
                      <div>
                        <div className="font-mono text-slate-700">{t.verifier_username}</div>
                        <div className="text-[10px] text-slate-500 uppercase">{t.verifier_provider || "—"}</div>
                      </div>
                    ) : t.status === "FAILED" && t.reason ? (
                      <div className="text-rose-700" title={t.reason}>{t.reason.slice(0, 28)}{t.reason.length > 28 ? "…" : ""}</div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`proof-cell-${t.reference}`}>
                    {t.proof_photo_url ? (
                      <ProofThumb url={t.proof_photo_url} reference={t.reference} />
                    ) : (
                      <span className="text-[11px] text-slate-400 italic">No proof</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3">
                    {t.status === "PENDING" ? (
                      <button data-testid={`verify-btn-${t.reference}`}
                        onClick={() => setVerifying(t)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-[#0F62FE] text-white text-[11px] font-semibold hover:bg-[#0050E6]">
                        <ShieldCheck className="w-3 h-3" /> Verify
                      </button>
                    ) : t.status === "SUCCESS" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Confirmed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700"><XCircle className="w-3 h-3" /> Rejected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {verifying && (
        <VerifyModal currentUser={user} tx={verifying} onClose={() => setVerifying(null)} onDone={() => { setVerifying(null); load(); loadVerifiers(); loadDashboard(); }} />
      )}

      {editingVerifier && (
        <VerifierEditModal
          payload={editingVerifier}
          onClose={() => setEditingVerifier(null)}
          onDone={() => { setEditingVerifier(null); loadVerifiers(); }}
        />
      )}
    </div>
  );
}

function VerifierEditModal({ payload, onClose, onDone }) {
  const [username, setUsername] = useState(payload.username || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(payload.name || "");
  const [phone, setPhone] = useState(payload.phone || "");
  const [commissionPct, setCommissionPct] = useState(((payload.commission_rate ?? 0.05) * 100).toFixed(1));
  const [balanceCredit, setBalanceCredit] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawNote, setWithdrawNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const isUpdate = !!payload.isUpdate;
  const verifierId = payload.id;
  const currentBalance = Number(payload.balance || 0);
  // E.164: leading '+', digits only, 7–15 digits total after country prefix.
  const E164_RE = /^\+[1-9]\d{6,14}$/;
  const hasPhoneOnFile = !!(payload.phone && payload.phone.trim());

  const submit = async () => {
    if (!username.trim() || username.trim().length < 3) { toast.error("Username must be at least 3 characters"); return; }
    if (!isUpdate && (!password || password.length < 6)) { toast.error("Password must be at least 6 characters"); return; }
    // Phone validation:
    //   * Create           → REQUIRED and must be valid E.164
    //   * Update (no phone yet on file) → REQUIRED
    //   * Update (has phone) → optional; if provided must be valid E.164
    const trimmedPhone = phone.trim();
    if (!isUpdate && !trimmedPhone) {
      toast.error("Phone number is required — the verifier receives the login code by SMS.");
      return;
    }
    if (isUpdate && !hasPhoneOnFile && !trimmedPhone) {
      toast.error("Phone number is required — this verifier has no number on file yet.");
      return;
    }
    if (trimmedPhone && !E164_RE.test(trimmedPhone)) {
      toast.error("Phone must be in international E.164 format, e.g. +50937123456");
      return;
    }
    const rate = parseFloat(commissionPct);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error("Commission rate must be 0-100"); return; }
    const credit = balanceCredit ? parseFloat(balanceCredit) : 0;
    if (balanceCredit && (isNaN(credit) || credit < 0)) { toast.error("Balance credit must be a positive number"); return; }
    setBusy(true);
    try {
      await api.post("/admin/cash-verifiers", {
        provider: payload.provider,
        username: username.trim().toLowerCase(),
        password: password || null,
        name: name.trim() || null,
        phone: trimmedPhone || null,
        commission_rate: rate / 100,
        balance_credit: credit > 0 ? credit : null,
      });
      toast.success(`${payload.provider === "moncash" ? "MonCash" : "NatCash"} verifier saved`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const submitWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount to withdraw"); return; }
    if (amt > currentBalance) { toast.error(`Verifier only has ${Math.round(currentBalance).toLocaleString("en-US")} ${payload.balance_currency || "HTG"}`); return; }
    if (!withdrawNote.trim()) { toast.error("Please add a note explaining this withdrawal"); return; }
    if (!verifierId) { toast.error("Verifier user not found"); return; }
    setWithdrawing(true);
    try {
      await api.post("/cash-withdrawals", {
        user_id: verifierId,
        amount: amt,
        currency: payload.balance_currency || "HTG",
        note: withdrawNote.trim(),
      });
      toast.success(`Withdrew ${Math.round(amt).toLocaleString("en-US")} ${payload.balance_currency || "HTG"} from verifier`);
      setWithdrawAmount("");
      setWithdrawNote("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdraw failed");
    } finally { setWithdrawing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {isUpdate ? "Edit" : "Create"} {payload.provider === "moncash" ? "MonCash" : "NatCash"} verifier
        </div>
        <div className="text-xs text-slate-500 mb-4">
          This account can only see and verify {payload.provider === "moncash" ? "MonCash" : "NatCash"} transactions. They cannot access any other part of the platform.
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Username</label>
            <input data-testid="verifier-username-input" value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} placeholder="e.g. verifier_moncash" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{isUpdate ? "New password (leave blank to keep)" : "Password"}</label>
            <PasswordInput data-testid="verifier-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="At least 6 characters" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="(optional)" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Phone number <span className="text-rose-500">*</span>
            </label>
            <input
              data-testid="verifier-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputCls}
              placeholder="+50937123456"
              autoComplete="tel"
              maxLength={20}
            />
            <div className="text-[11px] text-slate-500 mt-1">
              International E.164 format (e.g. <code>+509</code> Haiti, <code>+56</code> Chile, <code>+1</code> Dom. Rep. / USA).
              The verifier receives the login code by SMS on this number.
              {isUpdate && hasPhoneOnFile && (
                <>
                  {" "}Currently on file: <strong>{payload.phone_masked || payload.phone}</strong>.
                </>
              )}
              {isUpdate && !hasPhoneOnFile && (
                <>
                  {" "}<span className="text-rose-600 font-semibold">
                    No number on file yet — add one now so this verifier can sign in.
                  </span>
                </>
              )}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Commission rate (%)</label>
            <input data-testid="verifier-commission-input" type="number" step="0.1" min="0" max="100" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} className={inputCls} placeholder="5.0" />
            <div className="text-[11px] text-slate-500 mt-1">Earnings on every confirmed transaction (% of amount received).</div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {isUpdate ? "Add to balance (HTG)" : "Initial balance (HTG)"}
            </label>
            <input data-testid="verifier-balance-input" type="number" step="0.01" min="0" value={balanceCredit} onChange={(e) => setBalanceCredit(e.target.value)} className={inputCls} placeholder="0" />
            {isUpdate && payload.balance != null && (
              <div className="text-[11px] text-slate-500 mt-1">
                Current balance: <strong>{Math.round(payload.balance).toLocaleString("en-US")} {payload.balance_currency || "HTG"}</strong>. Entered amount will be ADDED on save.
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="verifier-save-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {isUpdate ? "Save changes" : "Create verifier"}
            </button>
          </div>

          {/* Withdraw Cash (admin-only, available on existing verifiers) */}
          {isUpdate && (
            <div className="mt-4 pt-4 border-t border-slate-200" data-testid="verifier-withdraw-section">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-rose-700">Withdraw cash</span>
                <span className="text-[10px] text-slate-500">— pull funds back from the verifier wallet</span>
              </div>
              <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800 mb-2">
                Use this to correct mistaken funding or reduce the verifier&apos;s wallet balance. The amount returns to your Master Admin cash wallet.
                <br />
                Available: <strong>{Math.round(currentBalance).toLocaleString("en-US")} {payload.balance_currency || "HTG"}</strong>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  data-testid="verifier-withdraw-amount"
                  type="number" step="0.01" min="0" max={currentBalance}
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className={inputCls}
                  placeholder={`Amount (max ${Math.round(currentBalance).toLocaleString("en-US")})`}
                />
                <input
                  data-testid="verifier-withdraw-note"
                  value={withdrawNote}
                  onChange={(e) => setWithdrawNote(e.target.value)}
                  className={inputCls}
                  placeholder="Note / reason (required)"
                  maxLength={300}
                />
              </div>
              <div className="flex justify-end mt-2">
                <button
                  onClick={submitWithdraw}
                  disabled={withdrawing || currentBalance <= 0}
                  data-testid="verifier-withdraw-submit"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
                >
                  {withdrawing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Withdraw cash
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
